import { formatCurrency, getCurrencySymbol } from '@angular/common';
import { platformDisplayInfo } from '../interfaces/plan-display-info';
import {
  CallsPlatformPricing,
  MMSPricing,
} from '../interfaces/platform-pricing';
import { ProductType } from '../type-aliases/product-type';
import { AgentPricingModel } from './agent-pricing.model';
import { PlatformModel } from './platform.model';

/**
 * TODO: Clean this up and remove it
 * This was copied from the common/billing/util currency-format.ts
 * Rather than dealing with the library circular dependency issue
 */
const currencyFormat = {
  currencyCode: 'USD',
  display: 'symbol-narrow',
  digitsInfo: '1.0-3',
};
function formatPrice(price: number): string {
  const isWholeNumber = price % 1 === 0; // returns 0 if it's a whole number
  const currencySymbol = getCurrencySymbol(
    currencyFormat.currencyCode,
    'narrow'
  );

  if (isWholeNumber) {
    currencyFormat.digitsInfo = '1.0';
  } else {
    currencyFormat.digitsInfo = '1.2-3';
  }

  return formatCurrency(
    price,
    'en-US',
    currencySymbol,
    currencyFormat.currencyCode,
    currencyFormat.digitsInfo
  );
}

export class LeadCenterPlatformModel extends PlatformModel {
  public agent_price: number;
  public platform: ProductType = 'lead_center';
  public platformInfo = platformDisplayInfo[this.platform];
  public agentPricing: AgentPricingModel;
  public minutes: CallsPlatformPricing;
  public mms: MMSPricing;

  public displayText;

  constructor(platformPlan) {
    super(platformPlan);
    Object.assign(this, platformPlan);
    if (this.agents) {
      this.setAgentPricing(platformPlan.agentCount);
    }
    this.setDisplayText();
  }

  public get _usageInfo(): string[] {
    // eslint-disable-next-line max-len
    let minutes = `${
      this.minutes.additional_price > 0
        ? this.minutes.included_units
        : 'Unlimited'
    } local Lead Center minutes per month`;
    if (this.minutes.additional_price) {
      minutes += `; $${this.minutes.additional_price} each additional`;
    }

    // if there's NO unit price for mms (rather than a $0 unit price), treat that as a situation where is MMS is not
    // a feature on the plan.  it's a convention/assumption at this point; if that ever stops being true, we'll need to
    // somehow pull the agency or feature-list into this model so that we can check if mms is supported.
    let mms = '';
    if (this.mms.unit_price !== undefined && this.mms.unit_price !== null) {
      const mmsPrice =
        this.mms.unit_price > 0
          ? `$${this.mms.unit_price} per`
          : 'No surcharge for';
      mms = `; ${mmsPrice} multimedia message${
        this.mms.unit_price ? '' : 's'
      } (MMS)`;
    }

    return [
      ...this.agentTierStrings,
      minutes,
      `$${this.minutes.tollfree.additional_price} per toll-free minute${mms}`,
    ];
    // tslint:enable:max-line-length
  }

  public get _unitPrice(): number {
    return this.agent_price;
  }

  public get agentTierStrings(): string[] {
    if (this.agentPricing?.tiered_pricing) {
      return this.agentPricing.tiers.map((tier) => this.getTierString(tier));
    }
    return [];
  }

  // Initializes an instance of TieredPricing with platform pricing data and a given number of agents
  public setAgentPricing(agentCount?: number): void {
    this.agentPricing = new AgentPricingModel(this.agents, agentCount);
  }

  public getTierString(tier): string {
    const { starting_unit, ending_unit, price } = tier;
    const formattedPrice = formatPrice(price as number);
    if (starting_unit === ending_unit) {
      return `${ending_unit} ${
        (ending_unit !== 1 && 'agents') || 'agent'
      } at ${formattedPrice} per agent each month`;
    } else if (!ending_unit) {
      return `${starting_unit}+ agents at ${formattedPrice} per agent each month`;
    } else {
      return `${starting_unit} - ${ending_unit} agents at ${formattedPrice} per agent each month`;
    }
  }

  public tieredPricePerAgent(agentCount: number): number {
    return (
      this.agentPricing?.tiers?.find((tier) => {
        return (
          agentCount >= tier.starting_unit &&
          (tier.ending_unit === null || agentCount <= tier.ending_unit)
        );
      })?.price ?? this.agentPricing?.unit_price
    );
  }

  public setDisplayText() {
    this.displayText = {
      marketingCopyA: `Unlimited SMS and chat messages`,
      marketingCopyB: `Unlimited local softphone minutes`,
      marketingCopyC: `Warm and cold call transfer capabilities`,
    };
  }
}
